import {
  createSlice,
  PayloadAction,
  createAsyncThunk
} from '@reduxjs/toolkit';
import { getCurrentUserInfo } from '@/common/api/common';
import { getUserMenu } from '@/common/api/home';
interface InitState {
  number: number;
  routerGuard: RouterGuard;
  /** 当前用户信息 */
  currentUserInfo: any;
  selectedTargetPoi: any;
  provinceCityDistrict: any[]; // 省市区
  provincesCities: any[]; // 省市
  citiesWithEN: any[]; // 带有拼音的城市列表
  userMenu:any[];
  // 专供高德地图的城市数据，数据格式
  // [{name: 城市名称, id: 城市id, provinceName: 所属省份名称, provinceId: 所属省份id, district: [] 区数据}]
  cityForAMap: any[];
  tenantInfo: any;
  /** 当前租户的检查信息 */
  tenantCheckInfo: any,

  /** 从其他页面进入到青春版地图时带入的筛选条件，使用后记得清除 */
  youthMapSearchParams: any,

  /** 是否需要在返回上一页时刷新当前页面 */
  needRefreshOnBack: boolean,
}

interface RouterGuard {
  from: any;
  to: any;
}

const initialState: InitState = {
  number: 0,
  // 注意目前该项目没有切换租户的操作，故没有添加清除currentUserInfo的逻辑
  /** 当前用户信息 */
  currentUserInfo: null,
  routerGuard: { // 路由守卫
    from: {},
    to: {}
  },
  selectedTargetPoi: null, // 到公共地图poi搜索页时选中的搜索结果
  provinceCityDistrict: [], // 省市区
  provincesCities: [], // 省市
  citiesWithEN: [], // 带有拼音的城市列表
  // 专供高德地图的城市数据，数据格式
  cityForAMap: [],
  userMenu: [],
  tenantInfo: null,

  /** 当前租户的检查信息 */
  tenantCheckInfo: {},

  /** 从其他页面进入到青春版地图时带入的筛选条件，使用后记得清除 */
  youthMapSearchParams: null,

  needRefreshOnBack: false,

};

const customerSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    changeCount: (state = initialState, action: PayloadAction<number>) => {
      state.number = action.payload;
    },
    /** 更新当前租户信息 */
    changeTenantInfo: (state = initialState, action: PayloadAction<any>) => {
      state.tenantInfo = action.payload;
    },
    storeRouterAfterEach: (state = initialState, action: PayloadAction<RouterGuard>) => {
      state.routerGuard = action.payload;
    },
    // 公共的地图poi搜索页时，搜索后选中的poi数据
    selectTargetSearchPoi: (state = initialState, action: PayloadAction<any>) => {
      state.selectedTargetPoi = action.payload;
    },
    changeProvinceCityDistrict: (
      state = initialState,
      action: PayloadAction<any[]>,
    ) => {
      const _cityForAMap: any[] = [];
      // 禁选特殊的行政区
      state.provinceCityDistrict = action.payload.map((item: any) => {
        if (
          ['台湾', '香港', '澳门'].some(
            (region) => item.name.indexOf(region) !== -1,
          )
        ) {
          item.children?.forEach((city: any) => {
            city.disabled = true;
            city.children?.forEach((district: any) => {
              district.disabled = true;
            });
            // cityForAMap数据组装
            _cityForAMap.push({
              name: city.name,
              id: city.id,
              code: city.code, // 对应高德数据中城市的adcode
              provinceName: item.name,
              provinceId: item.id,
              provinceCode: item.code, // 对应高德数据中省份的adcode
              district: city.children,
              disabled: true,
            });
          });
          return {
            ...item,
            disabled: true,
          };
        }
        // cityForAMap数据组装
        item.children?.forEach((city: any) => {
          _cityForAMap.push({
            name: city.name,
            id: city.id,
            code: city.code, // 对应高德数据中城市的adcode
            provinceName: item.name,
            provinceId: item.id,
            provinceCode: item.code, // 对应高德数据中省份的adcode
            district: city.children,
          });
        });
        return item;
      });
      state.cityForAMap = _cityForAMap;
    },
    changeProvincesCities: (
      state = initialState,
      action: PayloadAction<any[]>,
    ) => {
      state.provincesCities = action.payload.map((item: any) => {
        if (
          ['台湾', '香港', '澳门'].some(
            (region) => item.name.indexOf(region) !== -1,
          )
        ) {
          item.children?.forEach((city: any) => {
            city.disabled = true;
          });
          return {
            ...item,
            disabled: true,
          };
        }
        return item;
      });
    },
    changeCitiesWithEN: (
      state = initialState,
      action: PayloadAction<any[]>,
    ) => {
      state.citiesWithEN = action.payload.filter((item: any) => {
        return !['台湾', '香港', '澳门'].some(
          (region) => item.name.indexOf(region) !== -1,
        );
      });
    },

    setUserMenu: (
      state = initialState,
      action: PayloadAction<any[]>
    ) => {
      state.userMenu = action.payload;
    },

    /** 退出登录 */
    LOGOUT() {

    },

    /** 更新当前用户信息 */
    UPDATE_CURRENT_USER_INFO(state: any, action: any) {
      state.currentUserInfo = action.payload || {};
    },

    /** 更新当前租户的检查信息 */
    UPDATE_TENANT_CHECK_INFO(state: any, action: any) {
      state.tenantCheckInfo = action.payload || {};
    },

    /** 从其他页面进入到青春版地图时带入的筛选条件，使用后记得清除 */
    UPDATE_YOUTH_MAP_SEARCH_PARAMS(state, action: any) {
      state.youthMapSearchParams = action.payload || null;
    },

    /** 是否需要刷新列表（再返回上一页时） */
    SET_NEED_REFRESH_ON_BACK(state, action: any) {
      state.needRefreshOnBack = action.payload || false;
    },

  },
  extraReducers(builder) {
    // 获取用户信息请求成功
    builder
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.currentUserInfo = action.payload;
      });
    // 获取用户菜单
    builder.addCase(fetchUserMenu.fulfilled, (state, action) => {
      state.userMenu = action.payload;
    });
    // 如果需要知道请求状态或其他异步请求，可以链式调用继续 .addCase(xxx, () => {})..addCase(xxx, () => {})...
  },
});

// 导出action
export const {
  changeCount,
  storeRouterAfterEach,
  selectTargetSearchPoi,
  changeProvincesCities,
  changeProvinceCityDistrict,
  changeCitiesWithEN,
  setUserMenu,
  changeTenantInfo, // tip: 使用须知，state可能因为首页跳转新开了webview导致store丢失，可能需要在跳转后dispatch才能使用
  LOGOUT,
  UPDATE_CURRENT_USER_INFO,
  UPDATE_TENANT_CHECK_INFO,
  UPDATE_YOUTH_MAP_SEARCH_PARAMS,
  SET_NEED_REFRESH_ON_BACK,
} = customerSlice.actions;

// 获取当前用户信息
export const fetchCurrentUser = createAsyncThunk('currentUserInfo', async () => {
  const { data } = await getCurrentUserInfo();
  return data;
});

// 获取当前用户菜单
export const fetchUserMenu = createAsyncThunk('userMenu', async () => {
  // 调用获取用户菜单的 API
  const { data } = await getUserMenu();
  return data; //
});

// 提供给useSelector使用，用于获取当前用户信息
export const useInfo = (state) => state.common.currentUserInfo;
// 提供给useSelector使用，用于获取当前用户菜单
export const userMenu = (state) => state.common.userMenu;

export default customerSlice.reducer;
